import makeStyles from "@mui/styles/makeStyles";
import { addDays, addMinutes, differenceInMinutes, getDay, isValid, parseISO, startOfDay } from "date-fns";
import format from "date-fns/format";
import isAfter from "date-fns/isAfter";
import type { TFunction } from "i18next";
import * as Yup from "yup";

import { isCutoffClosingTooSoon } from "../../../../../utils/acceptance-cutoff";
import {generateDaysOfTheWeek, isSingleDate} from "../../../../../utils/helpers";
import { LOCATIONS, WORK_CENTERS } from "../../../../filters/constants";
import type { AppliedFilter } from "../../../../filters/filter-context";
import { DEFAULT_SHIFT_TRANSPORT_TYPE } from "../../../../transport/store/transport-types";
import { SchedulerWorkCenter } from "../../../store/types";

interface WeekDayDates {
	[key: string]: Date;
}

// Last-resort shift length when neither the location nor the company sets one.
const DEFAULT_SHIFT_DURATION_HRS = 2;

export const useStyles = makeStyles(() => ({
	cancelButton: {
		marginRight: "auto",
	},
	dialogContentRoot: {
		maxWidth: "70%",
	},
	formControlRoot: {
		width: "100%",
		"& .MuiFilledInput-root": {
			background: "#F8F8F8 !important",
		},
	},
	btnLabel: {
		position: "relative",
	},
	progressIndicator: {
		position: "absolute",
		left: "50%",
	},
	formRoot: {
		width: "100%",
	},
}));

const toLocalISOString = (date: Date) => {
	if (!isValid(date)) {
		return "";
	}
	const off = date.getTimezoneOffset();
	return new Date(
		date.getFullYear(),
		date.getMonth(),
		date.getDate(),
		date.getHours(),
		date.getMinutes() - off,
		date.getSeconds(),
		date.getMilliseconds()
	).toISOString();
};

export const setToNearestTime = (dateString: string) => {
	if (!dateString) return "";
	const date = new Date(dateString);
	if (!isValid(date)) return dateString;
	return toLocalISOString(date).slice(0, 16);
};

const toFormDateTime = (date: Date) =>
	format(date, "yyyy-MM-dd") + "T" + format(date, "HH:mm");

// Shift durations are configured in hours but can be fractional (7.5), so the
// default is applied in minutes - adding hours would silently truncate.
export const addDurationHrs = (date: Date, durationHrs?: number) =>
	addMinutes(date, Math.round((durationHrs || DEFAULT_SHIFT_DURATION_HRS) * 60));

// The end time a start time implies for a given default shift duration, in the
// "yyyy-MM-ddTHH:mm" shape the form fields carry.
export const applyDefaultShiftDuration = (
	startTime: string,
	durationHrs?: number
): string => {
	const start = new Date(startTime);
	if (!isValid(start)) return startTime;
	return toFormDateTime(addDurationHrs(start, durationHrs));
};

// The Location-Workcenter the Create Shift dialog opens on. A supervisor who
// has filtered the scheduler is already working inside that slice, so pick the
// first Work Center it leaves visible; otherwise fall back to the first one
// they have access to. Selecting up front is what lets the dialog offer times
// that are already bounded by that location's duration settings.
export const resolveDefaultWorkcenter = (
	workcenters: SchedulerWorkCenter[],
	appliedFilters?: AppliedFilter[]
): SchedulerWorkCenter | undefined => {
	if (!workcenters?.length) return undefined;

	const filteredIds = (type: string) =>
		appliedFilters
			?.find((filter) => filter.type === type)
			?.data?.map((option) => Number(option.id)) ?? [];

	const workcenterIds = filteredIds(WORK_CENTERS);
	if (workcenterIds.length) {
		const match = workcenters.find((wc) => workcenterIds.includes(Number(wc.id)));
		if (match) return match;
	}

	const locationIds = filteredIds(LOCATIONS);
	if (locationIds.length) {
		const match = workcenters.find((wc) =>
			locationIds.includes(Number(wc.locationId))
		);
		if (match) return match;
	}

	return workcenters[0];
};

function getWeekDaysFromStartDay(startDay: string): string[] {
	const weekDays = [
		"monday",
		"tuesday",
		"wednesday",
		"thursday",
		"friday",
		"saturday",
		"sunday",
	];

	const normalizedStartDay = startDay.toLowerCase();

	const startIndex = weekDays.indexOf(normalizedStartDay);

	if (startIndex === -1) {
		throw new Error("Invalid weekday provided");
	}

	return [...weekDays.slice(startIndex), ...weekDays.slice(0, startIndex)];
}

function getWeekDayDates(date: string | Date, startDay: string): WeekDayDates {
	// Map day names to date-fns day numbers (0-6, where 0 is Sunday)
	const dayMapping: { [key: string]: number } = {
		sunday: 0,
		monday: 1,
		tuesday: 2,
		wednesday: 3,
		thursday: 4,
		friday: 5,
		saturday: 6,
	};

	// Convert input date to Date object if it's a string
	const inputDate = typeof date === "string" ? parseISO(date) : date;

	// Validate date
	if (!isValid(inputDate)) {
		throw new Error("Invalid date provided");
	}

	// Validate start day
	const normalizedStartDay = startDay.toLowerCase();
	if (!(normalizedStartDay in dayMapping)) {
		throw new Error("Invalid weekday provided");
	}

	// Get ordered week days starting from the given day
	const weekDays = getWeekDaysFromStartDay(startDay);

	// Calculate the start of the week based on the input date and start day
	const currentDayNum = getDay(inputDate);
	const targetDayNum = dayMapping[normalizedStartDay];
	const daysToSubtract = (currentDayNum - targetDayNum + 7) % 7;
	const weekStartDate = startOfDay(addDays(inputDate, -daysToSubtract));

	// Create the mapping of days to dates
	return weekDays.reduce((acc, day, index) => {
		const currentDate = addDays(weekStartDate, index);
		acc[day] = new Date(currentDate);
		return acc;
	}, {} as WeekDayDates);
}

export const configureDatesOfTheWeek = (
	date: Date,
	weekStartDay: string
): Record<string, Date> => {
	const currentWeekDates = getWeekDayDates(date, weekStartDay.toLowerCase());
	return currentWeekDates;
};

export const getYupInitObject = (t: TFunction) => ({
	startTime: Yup.string()
		.required(t("scheduler_start-time-required"))
		.test(
			"is-greater",
			t("scheduler_start-time-greater-current"),
			function (value: string | undefined) {
				return value ? isAfter(new Date(value), new Date()) : !!value;
			}
		),
	endTime: Yup.string()
		.required(t("scheduler_end-time-required"))
		.test(
			"is-greater",
			t("scheduler_end-time-greater-start"),
			function (value: string | undefined) {
				const { startTime } = this.parent || {};
				return value ? isAfter(new Date(value), new Date(startTime)) : !!value;
			}
		),
	workCenterId: Yup.string().required(t("scheduler_work-center-required")),
	//jobId: Yup.string().required("Job Role is required"),
	numberOfWorkersNeeded: Yup.number()
		.moreThan(0, t("scheduler_min-worker-required"))
		.lessThan(100000, t("scheduler_max-worker-required"))
		.integer(t("scheduler_must-be-integer"))
		.required(t("scheduler_workers-required")),
	shiftPremium: Yup.string(),
	repeatShift: Yup.boolean(),
	shiftNote: Yup.string().max(250, t("scheduler_note-max-length")),
	isShiftAcceptanceCutoffEnabled: Yup.boolean(),
	minHoursBeforeShiftStartToAccept: Yup.number().when(
		"isShiftAcceptanceCutoffEnabled",
		{
			is: true,
			then: Yup.number()
				.required(t("scheduler_acceptance-cutoff-required"))
				.min(0.5, t("scheduler_acceptance-cutoff-range"))
				.max(168, t("scheduler_acceptance-cutoff-range"))
				.test(
					"cutoff-leaves-time-to-accept",
					t("scheduler_acceptance-cutoff-too-soon"),
					function (value) {
						const { startTime, isShiftAcceptanceCutoffEnabled } = this.parent;
						if (!isShiftAcceptanceCutoffEnabled || !startTime || !value) {
							return true;
						}
						return !isCutoffClosingTooSoon(startTime, value);
					}
				),
		}
	),
	isFlexiSignupEnabled: Yup.boolean(),
	minFlexiSignupHours: Yup.number().when("isFlexiSignupEnabled", {
		is: true,
		then: Yup.number()
			.required(t("scheduler_min-signup-duration-required"))
			.integer(t("scheduler_must-be-integer"))
			.min(1, ({ min }) =>
				t("scheduler_min-signup-duration-hours", {
					count: min,
					unit: min > 1 ? t("scheduler_hours-unit") : t("scheduler_hour-unit"),
				})
			)
			.test(
				"max-shift-duration",
				t("scheduler_min-signup-less-than-total"),
				function (value) {
					const { startTime, endTime } = this.parent;
					if (!startTime || !endTime || !value) return true;
					const shiftDurationHours = differenceInMinutes(
						new Date(endTime),
						new Date(startTime)
					) / 60;
					return (value ?? 0) < shiftDurationHours;
				}
			),
	}),
	repeatOn: Yup.object({
		0: Yup.boolean(),
		1: Yup.boolean(),
		2: Yup.boolean(),
		3: Yup.boolean(),
		4: Yup.boolean(),
		5: Yup.boolean(),
		6: Yup.boolean(),
	}).test(
		"at-least-one-day-selected",
		t("scheduler_at-least-one-day"),
		function (value: any) {
			const { repeatShift } = this.parent || {};
			// If repeatShift toggle is false, we do not validate days at all
			if (!repeatShift) return true;
			// If repeatShift toggle is true then we validate that at least one day is true
			else {
				const flag = !!(
					value &&
					(value[0] ||
						value[1] ||
						value[2] ||
						value[3] ||
						value[4] ||
						value[5] ||
						value[6])
				);
				return flag;
			}
		}
	),
});

type Skill = {
	skillId: string;
	skillLevelId: string;
	skillName: string;
	level: number;
	id: string;
	name: string;
};
interface InitValues {
	startTime: string;
	endTime: string;
	jobId: string;
	workCenterId: string;
	numberOfWorkersNeeded: string;
	shiftPremium: string;
	repeatShift: boolean;
	repeatOn: Record<number, boolean>;
	locationId: string;
	shiftSkills: Skill[] | [];
	hexColor: string;
	shiftNote: string;
	shiftPurposeId: number | string;
	shiftPurposeDetail: string;
	repeatEndDate: string;
	endType: "OnDate" | "AfterOccurrences";
	occurrences: number | null;
	allowPartialSignups: boolean;
	isFlexiSignupEnabled: boolean;
	minFlexiSignupHours: number;
	isShiftAcceptanceCutoffEnabled: boolean;
	minHoursBeforeShiftStartToAccept: number;
	allowedWorkerTypeIds: number[] | null;
	isUrgent: boolean;
	// What the shift offers: "P" pickup, "D" drop-off, "B" both, "" none.
	transportType: "" | "P" | "D" | "B";
}

export const getInitValues = ({
  draftShiftDate,
  defaultDurationHrs,
  shiftPurposeId,
  weekStartDay,
}: {
  draftShiftDate: Date | { startDate: Date; endDate: Date };
  defaultDurationHrs?: number;
  shiftPurposeId: number;
  weekStartDay: string;
}): InitValues => {
	const shiftDay: number = isSingleDate(draftShiftDate)
	? draftShiftDate.getDay() : draftShiftDate.startDate.getDay();
	const initDays: Record<number, boolean> = {
		0: false,
		1: false,
		2: false,
		3: false,
		4: false,
		5: false,
		6: false,
		[shiftDay]: true,
	};
	const weekEnd = generateDaysOfTheWeek(isSingleDate(draftShiftDate) ? draftShiftDate : draftShiftDate.startDate, weekStartDay);
	const startTime = isSingleDate(draftShiftDate) ? draftShiftDate : draftShiftDate.startDate;
	const endTime = isSingleDate(draftShiftDate) ? addDurationHrs(draftShiftDate, defaultDurationHrs) : draftShiftDate.endDate;

	return {
		startTime:
			format( startTime , "yyyy-MM-dd") +
			"T" +
			format( startTime , "HH:mm"),
		endTime:
			format( endTime , "yyyy-MM-dd") +
			"T" +
			format( endTime , "HH:mm"),
		jobId: "",
		workCenterId: "",
		locationId: "",
		numberOfWorkersNeeded: "",
		shiftPremium: "",
		repeatShift: false,
		shiftSkills: [],
		repeatOn: { ...initDays },
		hexColor: "",
		shiftNote: "",
		shiftPurposeId: shiftPurposeId === 0 ? "" : shiftPurposeId,
		shiftPurposeDetail: "",
		repeatEndDate: format(weekEnd.sunday, "yyyy-MM-dd"),
		endType: "OnDate",
		occurrences: null,
		allowPartialSignups: false,
		isFlexiSignupEnabled: false,
		minFlexiSignupHours: 1,
		// Resolution against the Work Center / Location happens in the form; the
		// Shift only carries a value once the supervisor overrides it.
		isShiftAcceptanceCutoffEnabled: false,
		minHoursBeforeShiftStartToAccept: 0,
		allowedWorkerTypeIds: null,
		isUrgent: false,
		// A new shift offers both legs to start with; the supervisor narrows
		// it or switches it off. Harmless where transport is not switched on:
		// the field is only ever read at a location that does transport.
		transportType: DEFAULT_SHIFT_TRANSPORT_TYPE,
	};
};

export function getDaysOfWeek(repeatOn: { [key: string]: boolean }): string {
	const daysOfWeek = [
		"Sunday",
		"Monday",
		"Tuesday",
		"Wednesday",
		"Thursday",
		"Friday",
		"Saturday",
	];
	return Object.entries(repeatOn)
		.filter(([_, isSelected]) => isSelected)
		.map(([dayIndex]) => daysOfWeek[parseInt(dayIndex)])
		.join(",");
}
